import {
  allSafetyAssets,
  getSafetyAsset,
  getSafetyAssetCategoryValue,
  safetyAssetCategories,
} from './safetyAssetTypes';

export const HEALTH_SAFETY_CATEGORY = {
  EMERGENCY_EQUIPMENT: 'emergency_equipment',
  EVACUATION_SAFETY_POINTS: 'evacuation_safety_points',
  HAZARDS_RISK_AREAS: 'hazards_risk_areas',
  ROADS: 'roads',
  ACCESS_ENTRY: 'access_entry',
  RESOURCES_INFRASTRUCTURE: 'resources_infrastructure',
  DOCUMENTATION_TRAINING: 'documentation_training',
};

export const HEALTH_SAFETY_TYPE_VALUES = {
  FIRE_EXTINGUISHER: 'emergency_equipment__fire_extinguisher',
  FIRE_BLANKET: 'emergency_equipment__fire_blanket',
  FIRST_AID_KIT: 'emergency_equipment__first_aid_kit',
  EVACUATION_POINT: 'evacuation_safety_points__evacuation_point_assembly_area',
};

const normalizeLegacyKey = (value) => {
  if (!value) {
    return '';
  }
  return `${value}`
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
};

const LEGACY_TYPE_ENTRIES = [
  ['fireExtinguisher', HEALTH_SAFETY_TYPE_VALUES.FIRE_EXTINGUISHER],
  ['fire_extinguisher', HEALTH_SAFETY_TYPE_VALUES.FIRE_EXTINGUISHER],
  ['fireBlanket', HEALTH_SAFETY_TYPE_VALUES.FIRE_BLANKET],
  ['fire_blanket', HEALTH_SAFETY_TYPE_VALUES.FIRE_BLANKET],
  ['firstAidKit', HEALTH_SAFETY_TYPE_VALUES.FIRST_AID_KIT],
  ['first_aid_kit', HEALTH_SAFETY_TYPE_VALUES.FIRST_AID_KIT],
  ['evacuationPoint', HEALTH_SAFETY_TYPE_VALUES.EVACUATION_POINT],
  ['evacuation_point', HEALTH_SAFETY_TYPE_VALUES.EVACUATION_POINT],
  ['policy', 'documentation_training__policy'],
  ['induction', 'documentation_training__induction'],
  ['procedure', 'documentation_training__procedure'],
];

const LEGACY_TYPE_MAP = new Map(
  LEGACY_TYPE_ENTRIES.map(([legacyValue, targetValue]) => [
    normalizeLegacyKey(legacyValue),
    targetValue,
  ])
);

export const healthSafetyTypes = allSafetyAssets.map((asset) => ({
  label: asset.label,
  value: asset.value,
  category: asset.category ?? getSafetyAssetCategoryValue(asset.value),
}));

const healthSafetyTypeMap = new Map(
  healthSafetyTypes.map((type) => [type.value, type])
);

export const normalizeHealthSafetyType = (value) => {
  if (!value) {
    return value;
  }
  const normalizedLegacyValue = LEGACY_TYPE_MAP.get(normalizeLegacyKey(value));
  if (normalizedLegacyValue) {
    return normalizedLegacyValue;
  }
  return value;
};

export const getHealthSafetyType = (value) => {
  const normalized = normalizeHealthSafetyType(value);
  if (!normalized) {
    return null;
  }
  return healthSafetyTypeMap.get(normalized) ?? getSafetyAsset(normalized) ?? null;
};

export const getHealthSafetyTypeLabel = (value) => {
  return getHealthSafetyType(value)?.label ?? value;
};

export const getHealthSafetyTypeCategory = (value) => {
  const type = getHealthSafetyType(value);
  if (type?.category) {
    return type.category;
  }
  return getSafetyAssetCategoryValue(normalizeHealthSafetyType(value));
};

export const isDocumentationType = (value) => {
  return getHealthSafetyTypeCategory(value) === HEALTH_SAFETY_CATEGORY.DOCUMENTATION_TRAINING;
};

export const isFireExtinguisherType = (value) => {
  return normalizeHealthSafetyType(value) === HEALTH_SAFETY_TYPE_VALUES.FIRE_EXTINGUISHER;
};

export const isFirstAidKitType = (value) => {
  return normalizeHealthSafetyType(value) === HEALTH_SAFETY_TYPE_VALUES.FIRST_AID_KIT;
};

export const requiresTags = (value) => {
  return isDocumentationType(value);
};

export const requiresLocation = (value) => {
  return !isDocumentationType(value);
};

export const requiresManufactureDate = (value) => {
  return isFireExtinguisherType(value) || isFirstAidKitType(value);
};

export const requiresCreationDate = (value) => {
  return !requiresManufactureDate(value);
};

export const requiresLastCheckedDate = (value) => {
  return !isFireExtinguisherType(value);
};

export const getHealthSafetyTypeOptionsByCategory = () => {
  return safetyAssetCategories.map((category) => ({
    label: category.label,
    value: category.value,
    options: category.hazards.map((hazard) => ({
      label: hazard.label,
      value: hazard.value,
    })),
  }));
};

export const legacyHealthSafetyTypeMap = new Map(LEGACY_TYPE_MAP);
